<script setup>
import { ref, computed } from 'vue'

import { getI18n } from '@/plugins/i18n'

import { DxDataGrid } from 'devextreme-vue/data-grid'
import DxList from 'devextreme-vue/list'
import DxSelectBox from 'devextreme-vue/select-box'
import DxNumberBox from 'devextreme-vue/number-box'
import DxButton from 'devextreme-vue/button'

import ChipStatus from '@/components/shared/ChipStatus.vue'
import SupersessionProductDropDownBox from '@/components/shared/SupersessionProductDropDownBox.vue'

import { useNotify } from '@/composables/notify'
import * as dataGrid from '@/composables/data-grid'
import { useDialog } from '@/composables/dialog'
import { useLookup } from '@/composables/data-grid'
import { useImportRow } from '@/composables/import-row'
import { useImportRowValidation } from '@/composables/import-row-validation'
import { useImportRowApi } from '@/composables/import-row-workflow'

import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useSaleStore } from '@/stores/sale.store'
import { usePortalStore } from '@/stores/portal.store'

const props = defineProps({
  gridId: {
    type: String,
    default: null,
  },
  importActions: {
    type: Array,
    default: () => [],
  },
  columns: {
    type: Array,
    default: () => [],
  },
  dataSource: {
    type: [Array, Object],
    default: null,
  },
  documentType: {
    type: Number,
    default: null,
  },
  documentId: {
    type: Number,
    default: null,
  },
  entityLock: {
    type: Object,
    default: null,
  },
})
const emit = defineEmits(['saved', 'update:selectedRows', 'update:selectedKeys'])

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()
const saleStore = useSaleStore()
const portalStore = usePortalStore()

const { customDialog } = useDialog()
const {notifySuccess} = useNotify()
const { canEditQuantity } = useImportRow()

const {
  validationTypes,
  validationColor,
  validationLabel,
  validationTooltip,
  rowValidationsTooltip,
  sortValidationsBySeverity,
  worstValidationType,
  getFieldValidation,
} = useImportRowValidation()

const { revalidateRowAsync } = useImportRowApi(props)

const selectedRow = ref(null)
const selectedValidationId = ref(null)
const draftQuantity = ref(null)

const selectedRowKeys = ref([])
const selectionInitialized = ref(false)

const isSelectedRowExcluded = computed(() =>
  !!selectedRow.value && !selectedRowKeys.value.includes(keyOf(selectedRow.value))
)

let suppressNextReconcile = false

const setSelectedRow = (row) => {
  selectedRow.value = row || null
  selectedValidationId.value = null
  draftQuantity.value = null
}

const setSelectedValidationId = (validationId) => {
  selectedValidationId.value = validationId ?? null
  draftQuantity.value = null
}

const sortedValidations = computed(() => sortValidationsBySeverity(selectedRow.value?.validations))

const selectedValidation = computed(() =>
  sortedValidations.value.find(v => v.id === selectedValidationId.value) ?? sortedValidations.value[0] ?? null
)

const selectedValidationIndex = computed(() =>
  sortedValidations.value.findIndex(v => v.id === selectedValidation.value?.id)
)

const selectValidationByOffset = (offset) => {
  const validations = sortedValidations.value

  if (validations.length < 2) return

  const index = (selectedValidationIndex.value + offset + validations.length) % validations.length
  setSelectedValidationId(validations[index].id)
}

const isAddActionAllowed = computed(() =>
  props.documentType !== portalStore.documentTypesEnum.ORDER_SUPPLIER_ORDER
)

const selectedRowTitle = computed(() => {
  const row = selectedRow.value

  if (!row) return ''

  const code = row.code || row.product_code
  const name = row.description || row.name || row.product_name

  return [code, name].filter(Boolean).join(' - ')
})

const selectedControlConfig = computed(() => {
  const control = selectedValidation.value?.validation_control
  return (control && webSiteStore.importRowControlActionMapping[control]) || { uiType: 'none', actions: [] }
})

const validationActionUiType = computed(() => selectedControlConfig.value.uiType)

const availableActions = computed(() => {
  const control = selectedValidation.value?.validation_control
  if (!control) return []

  const actions = webSiteStore.getLocalizedImportRowActionOptions(control)
  if (isAddActionAllowed.value) return actions

  return actions.filter(action => action.id !== webSiteStore.importRowActionEnum.ADD)
})

const defaultAction = computed(() => isAddActionAllowed.value ? webSiteStore.importRowActionEnum.ADD : availableActions.value[0]?.id ?? null)

const selectedAction = computed(() => {
  const validation = selectedValidation.value

  if (validation?.validation_action != null) return validation.validation_action
  if (validation?.validation_type === validationTypes.ERROR) return null

  return defaultAction.value
})

const panelQuantity = computed(() => draftQuantity.value ?? selectedRow.value?.quantity ?? null)
const canEditSelectedRowQuantity = computed(() => canEditQuantity(selectedRow.value))

const adjustedQuantity = computed(() => {
  const row = selectedRow.value
  const control = selectedValidation.value?.validation_control

  if (!row) return null

  if (control === webSiteStore.importRowControlEnum.QUANTITY_NOT_MULTIPLE_OF_PACKAGE && row.package_quantity > 0) {
    return Math.max(Math.ceil((panelQuantity.value || 0) / row.package_quantity), 1) * row.package_quantity
  }

  if (control === webSiteStore.importRowControlEnum.MINIMUM_QUANTITY && row.minimum_quantity != null) {
    return Math.max(panelQuantity.value || 0, row.minimum_quantity)
  }

  return null
})

const canAdjustQuantity = computed(() => adjustedQuantity.value != null && adjustedQuantity.value !== panelQuantity.value)

const setValidationAction = (actionId) => {
  const validation = selectedValidation.value

  if (!validation) return

  validation.validation_action = actionId ?? webSiteStore.importRowActionEnum.ADD
  emit('update:selectedRows', selectedRow.value)
}

const applyRowPatchAsync = async(patch) => {
  if (!selectedRow.value) return

  const updatedRow = await revalidateRowAsync(selectedRow.value, patch)

  if (!updatedRow) return

  suppressNextReconcile = true
  setSelectedRow(updatedRow)
  emit('update:selectedRows', updatedRow)
}

const onNewProductSelected = (e) => applyRowPatchAsync({ product_ref: e.id })
const onUnitOfMeasureChanged = async(value) => {
  await applyRowPatchAsync({ unit_of_measure_ref: value })
  if (selectedRow.value?.product_ref < 0) {
    const result = await customDialog(i18n.t('common.updateUnitOfMeasureTitle'), i18n.t('common.updateUnitOfMeasureDescription'), i18n.t('common.yes'), i18n.t('common.no'))
    if (result === true) {
      const data = {
        unit_of_measure_ref: selectedRow.value?.unit_of_measure_ref,
        supplier_brand_ref: selectedRow.value?.supplier_brand_ref
      }
      await registryStore.api.updateProductAsync(selectedRow.value.product_ref, data)
      if (selectedRow.value?.source === webSiteStore.importRowTypesEnum.CLIPBOARD) {
        await saleStore.api.updateClipboardRowAsync(selectedRow.value.id, data)
      }

      notifySuccess(i18n.t('common.success_update'))
    }
  }
}
const onQuantityConfirmed = () => applyRowPatchAsync({ quantity: panelQuantity.value })
const onQuantityAdjusted = () => applyRowPatchAsync({ quantity: adjustedQuantity.value })

const { gridRef } = dataGrid.useGridRef()
defineExpose({ focusedRow: dataGrid.focusedRow, gridRef })

const keyOf = (row, instance = gridRef.value?.instance) => instance?.keyOf?.(row) ?? row?.id

const cols = [
  {
    caption: i18n.t('common.source'),
    dataField: 'source',
    lookup: useLookup({
      store: webSiteStore.getLocalizedImportRowTypes(),
    }),
    cellTemplate: 'importRowTypeCellTemplate',
    width: '6rem',
  },
  {
    caption: webSiteStore.blankCaption,
    dataField: 'validationStatus',
    cellTemplate: 'validationStatusCellTemplate',
    width: '2rem',
    alignment: 'center',
  },
  { dataField: 'keep_price_discount', dataType: 'boolean', caption: i18n.t('common.keepPriceDiscount', 2) },
  ...props.columns.filter(col => col.type !== 'buttons'),
]

const resetValidationCellStyle = (cellElement) => {
  cellElement.classList.remove(
    'import-validation-cell',
    'import-validation-cell--error',
    'import-validation-cell--warning',
  )

  cellElement.style.removeProperty('--import-validation-color')
}

const applyValidationCellStyle = (cellElement, validation) => {
  cellElement.classList.add('import-validation-cell')
  cellElement.style.setProperty('--import-validation-color', validationColor(validation.validation_type))

  if (validation.validation_type === validationTypes.ERROR) {
    cellElement.classList.add('import-validation-cell--error')
  } else if (validation.validation_type === validationTypes.WARNING) {
    cellElement.classList.add('import-validation-cell--warning')
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: props.gridId,
  columns: cols,
  ...(Array.isArray(props.dataSource) ? { keyExpr: 'id' } : {}),
  editing: { allowUpdating: false, mode: 'batch' },
  export: { enabled: false },
  selection: {
    mode: 'multiple',
    showCheckBoxesMode: 'always',
    allowSelectAll: true,
  },
  columnChooser: { enabled: false },
  scrolling: { mode: 'virtual' },
  focusedRowEnabled: true,
  entityLock: props.entityLock,
  entityLockReadOnlyDisabled: true,
  height: '100%',
  onContentReady: (e) => {
    const dataRows = e.component.getVisibleRows().filter(i => i.rowType === 'data')

    if (!dataRows.length) return

    if (!selectionInitialized.value) {
      selectionInitialized.value = true
      e.component.selectAll()
    } else {
      const availableKeys = new Set(dataRows.map(i => i.key))
      const currentSelected = new Set(e.component.getSelectedRowKeys())
      const keysToRestore = selectedRowKeys.value.filter(key => availableKeys.has(key))

      const missing = keysToRestore.filter(key => !currentSelected.has(key))

      if (missing.length) {
        e.component.selectRows(missing, true)
      }
    }

    if (!selectedRow.value) {
      e.component.option('focusedRowKey', dataRows[0].key)
      setSelectedRow(dataRows[0].data)
      return
    }

    if (suppressNextReconcile) {
      suppressNextReconcile = false
    } else {
      const current = dataRows.find(i => i.key === keyOf(selectedRow.value, e.component))
      if (current && current.data !== selectedRow.value) selectedRow.value = current.data
    }
  },
  onCellPrepared: (e) => {
    if (e.rowType !== 'data') return

    resetValidationCellStyle(e.cellElement)
    e.cellElement.removeAttribute('title')

    const validation = getFieldValidation(e.data?.validations, e.column?.dataField)

    if (!validation) return

    applyValidationCellStyle(e.cellElement, validation)
    e.cellElement.setAttribute('title', validationTooltip(validation))
  },
  onRowPrepared: (e) => {
    if (e.rowType !== 'data') return

    const isProductUnavailable = e.data?.product_availability === registryStore.productAvailabilitiesEnum.NOT_AVAILABLE
    e.rowElement.classList.toggle('dx-disabled-row', isProductUnavailable)
  },
  onFocusedRowChanged: (e) => {
    setSelectedRow(e.row?.data || null)
  },
  onSelectionChanged: (e) => {
    selectedRowKeys.value = e.selectedRowKeys ?? []
    emit('update:selectedKeys', selectedRowKeys.value)
  },
  onSaved: (e) => emit('saved', e),
  onToolbarPreparing: (e) => { e.toolbarOptions.items = [] },
})
</script>

<template>
  <div class="rig-layout column no-wrap">
    <div class="rig-layout__grid">
      <dx-data-grid
        ref="gridRef"
        v-bind="gridOptions"
        :data-source="dataSource"
        style="width: 100%; height: 100%;"
      >
        <template #importRowTypeCellTemplate="{ data: templateOptions }">
          <chip-status :data="webSiteStore.getLocalizedImportRowTypes().find(x => x.id === templateOptions.data.source)" />
        </template>

        <template #documentRowTypeCellTemplate="{ data: templateOptions }">
          <chip-status :data="webSiteStore.getLocalizedDocumentRowTypes().find(x => x.id === templateOptions.data.document_row_type)" />
        </template>

        <template #validationStatusCellTemplate="{ data }">
          <div
            class="rig-dot"
            :title="rowValidationsTooltip(data.data?.validations)"
            :style="{ backgroundColor: validationColor(worstValidationType(data.data?.validations)) }"
          />
        </template>
      </dx-data-grid>
    </div>

    <div class="rig-panel column no-wrap">
      <div class="rig-panel__header row items-center justify-between q-px-md q-py-xs">
        <div class="rig-panel__title text-weight-bold ellipsis">
          {{ selectedRowTitle }}
        </div>

        <div
          v-if="sortedValidations.length"
          class="rig-panel__counter row items-center no-wrap"
        >
          <dx-button
            icon="chevronleft"
            styling-mode="text"
            :disabled="isSelectedRowExcluded || sortedValidations.length < 2"
            @click="() => selectValidationByOffset(-1)"
          />
          <span>{{ i18n.t('common.importRow.validation') }} {{ selectedValidationIndex + 1 }} / {{ sortedValidations.length }}</span>
          <dx-button
            icon="chevronright"
            styling-mode="text"
            :disabled="isSelectedRowExcluded || sortedValidations.length < 2"
            @click="() => selectValidationByOffset(1)"
          />
        </div>
      </div>

      <div
        class="rig-panel__body row no-wrap"
        :class="{ 'rig-panel__body--excluded': isSelectedRowExcluded }"
      >
        <div class="rig-panel__section row q-px-md q-py-sm">
          <div class="rig-panel__section-label text-weight-bold">
            {{ i18n.t('common.importRow.detailsAndValidations') }}
          </div>

          <div
            v-if="!selectedRow"
            class="rig-panel__empty"
          >
            {{ i18n.t('common.importRow.selectRowHint') }}
          </div>

          <dx-list
            v-else
            :items="sortedValidations"
            key-expr="id"
            selection-mode="single"
            :selected-item-keys="selectedValidation ? [selectedValidation.id] : []"
            item-template="validationItemTemplate"
            width="100%"
            @item-click="({ itemData }) => setSelectedValidationId(itemData?.id)"
          >
            <template #validationItemTemplate="{ data: item }">
              <div
                class="rig-validation items-center"
                :class="{ 'rig-validation--selected': selectedValidation?.id === item.id }"
              >
                <div
                  class="rig-validation__dot"
                  :style="{ backgroundColor: validationColor(item.validation_type) }"
                />
                <div>{{ validationLabel(item) }}</div>
              </div>
            </template>
          </dx-list>
        </div>

        <div class="rig-panel__section rig-panel__section--right column no-wrap q-px-md q-py-sm">
          <div class="rig-panel__section-label text-weight-bold">
            {{ i18n.t('common.importRow.resolutionActions') }}
          </div>

          <dx-select-box
            v-if="validationActionUiType === 'select' && availableActions?.length"
            :value="selectedAction"
            :items="availableActions"
            display-expr="name"
            value-expr="id"
            :label="i18n.t('common.importRow.selectAction')"
            styling-mode="underlined"
            width="100%"
            @value-changed="({ value }) => setValidationAction(value)"
          />

          <div
            v-else-if="['supersession', 'not_available'].includes(validationActionUiType)"
            class="rig-supersession"
          >
            <supersession-product-drop-down-box
              :data="{ supersession_id: selectedRow?.product_ref }"
              @new-product-selected="onNewProductSelected"
            />
          </div>

          <div
            v-else-if="canEditSelectedRowQuantity && ['package', 'minimum'].includes(validationActionUiType)"
            class="rig-quantity column"
          >
            <div class="rig-quantity__row row items-center">
              <dx-number-box
                :value="validationActionUiType === 'package' ? selectedRow?.package_quantity : selectedRow?.minimum_quantity"
                :label="validationActionUiType === 'package' ? i18n.t('common.packageQuantity') : i18n.t('common.minimumQuantity')"
                styling-mode="underlined"
                :read-only="true"
                width="100%"
              />

              <dx-number-box
                :value="panelQuantity"
                :label="i18n.t('common.quantity')"
                styling-mode="underlined"
                :min="1"
                width="100%"
                @value-changed="({ value }) => { draftQuantity = value }"
              />
            </div>

            <div class="rig-quantity__actions row justify-end">
              <dx-button
                v-if="canAdjustQuantity"
                :text="i18n.t('common.importRow.adjustQuantityTo', { quantity: adjustedQuantity })"
                type="default"
                styling-mode="outlined"
                @click="onQuantityAdjusted"
              />

              <dx-button
                :text="i18n.t('common.importRow.verify')"
                type="default"
                styling-mode="contained"
                @click="onQuantityConfirmed"
              />
            </div>
          </div>

          <div v-else-if="validationActionUiType === 'missing_unit_measure'">
            <dx-select-box
              :value="selectedRow?.unit_of_measure_ref"
              :items="webSiteStore.getLocalizedUnitsOfMeasure()"
              display-expr="name"
              value-expr="id"
              :label="i18n.t('common.importRow.selectAction')"
              styling-mode="underlined"
              width="100%"
              @value-changed="({ value }) => onUnitOfMeasureChanged(value)"
            />
          </div>

          <div
            v-else
            class="rig-panel__empty"
          >
            {{ selectedValidation ? i18n.t('common.importRow.noActionAvailable') : i18n.t('common.importRow.selectValidation') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.import-validation-cell {
  position: relative;
  box-shadow: inset 0 0 0 9999px color-mix(in srgb, var(--import-validation-color) 16%, transparent);
  border-left: 3px solid var(--import-validation-color) !important;
}
.import-validation-cell--error { font-weight: 600; }
.import-validation-cell--warning { font-weight: 500; }
.rig-layout__grid .dx-data-row:not(.dx-selection) > td:not(.dx-command-select) { opacity: 0.5; filter: grayscale(1); }
.rig-layout { width: 100%; height: 600px; min-height: 0; }
.rig-layout__grid { flex: 1 1 50%; min-height: 0; }
.rig-panel { flex: 1 1 50%; min-height: 0; border-top: 1px solid rgba(0, 0, 0, 0.12); }
.rig-panel__header {
  gap: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background-color: rgba(0, 0, 0, 0.03);
}
.rig-panel__title { font-size: 13px; }
.rig-panel__counter { gap: 4px; font-size: 13px; }
.rig-panel__body {
  flex: 1;
  min-height: 0;
  transition: opacity 0.15s ease, filter 0.15s ease;
}
.rig-panel__body--excluded { opacity: 0.5; filter: grayscale(1); pointer-events: none; }
.rig-panel__section { gap: 8px; flex: 1 1 33.333%; min-width: 0; height: 100%; }
.rig-panel__section--right { flex-basis: 66.667%; flex-grow: 2; border-left: 1px solid rgba(0, 0, 0, 0.12); }
.rig-supersession { flex: 1 1 auto; width: 100%; min-width: 0; min-height: 0; overflow: hidden; }
.rig-panel__section-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #757575;
}
.rig-panel__empty { color: #757575; font-size: 12px; }
.rig-dot { width: 12px; height: 12px; border-radius: 50%; margin: 0 auto; }
.rig-panel .rig-validation { gap: 12px; padding: 8px; border-radius: 4px; display: flex;}
.rig-panel .rig-validation--selected { background-color: #e3f2fd; color: #1976d2; }
.rig-panel .rig-validation__dot { width: 12px; height: 12px; border-radius: 50%; }
.rig-quantity { gap: 12px; }
.rig-quantity__row { gap: 16px; }
.rig-quantity__actions { gap: 8px; }
.rig-quantity__actions .dx-button { white-space: nowrap; }
</style>
